import React from 'react'
import ReactTooltip from 'react-tooltip'
import useRebuildTooltipsHook from '../../../hooks/useRebuildTooltips'
import { ReactComponent as RunningIcon } from '../../../assets/svg/RunningIcon.svg'
import { ReactComponent as AnalyzeIcon } from '../../../assets/svg/analyze.svg'
import { ReactComponent as LockIconTrial } from '../../../assets/svg/LockIconTrial.svg'
import { notify, translateTargetText } from '../../../utils/util'

// Cards past this index share the same entrance delay so a long list never
// makes the user wait for the tail of the stagger (same cap as the classroom).
export const STAGGER_CAP = 12

export const ANALYSIS_TOOLTIP_ID = 'rtg-analysis-tip'
export const RUNNING_TOOLTIP_ID = 'rtg-running-tip'

/**
 * The single pair of tooltip instances shared by every card in a list.
 * Lists call `useRebuildTooltips(content)` so cards mounted later (show more,
 * new data) get bound too.
 */
export const ContentCardTooltips = () => (
    <>
        <ReactTooltip id={ANALYSIS_TOOLTIP_ID} arrowColor="#5E639B" effect="solid" backgroundColor="#5E639B" className="wellbeing-tooltip" />
        <ReactTooltip id={RUNNING_TOOLTIP_ID} arrowColor="#5E639B" effect="solid" backgroundColor="#5E639B" className="wellbeing-tooltip" />
    </>
)

// Re-exported so existing list imports keep working; the hook lives in src/hooks.
export const useRebuildTooltips = useRebuildTooltipsHook

export const getContentKey = (item, index) => item?.id ?? item?._id ?? `content-${index}`

const runningMessage = (item, t) => {
    const targets = item?.runningPrograms?.map((target) => translateTargetText(target?.target_text)).join(', ') ?? ''
    return `${t('currentlyRunning')}: ${targets}`
}

/**
 * One ready-to-go content tile. Memoized: lists re-render on every search
 * keystroke and only the filtered set should change, not every tile.
 */
const ContentCard = React.memo(function ContentCard({ item, index = 0, className = '', iconFill = '#5E639B', onSelect, onOpenRunning, tabIndex, t }) {
    const handleClick = () => {
        if (item?.locked) notify('This content is locked due to Trial Version')
        else onSelect(item)
    }

    return (
        <button
            type="button"
            className={`single-type-section-item rtg-card ${className}`}
            style={{ '--i': Math.min(index, STAGGER_CAP) }}
            onClick={handleClick}
            tabIndex={tabIndex}
        >
            <div className="flex flex-row items-center mb-[10px] w-full">
                <div className="flex-1 flex flex-row items-center min-w-0">
                    <p className="single-type-item-title text-left">{item?.name ?? item?.title}</p>
                    {item?.locked && (
                        <span className="mx-[11px]">
                            <LockIconTrial />
                        </span>
                    )}
                </div>
                {!!item?.surveyAnalysis && (
                    <span data-tip={t('thisSurveyCanBeAnalyzed')} data-for={ANALYSIS_TOOLTIP_ID} className="mx-[11px] flex items-center">
                        <AnalyzeIcon width={22} height={22} fill={iconFill} />
                    </span>
                )}
                {!!item?.runningPrograms && (
                    <span
                        role="button"
                        aria-label={t('currentlyRunning')}
                        className="rtg-running-badge flex items-center"
                        data-tip={runningMessage(item, t)}
                        data-for={RUNNING_TOOLTIP_ID}
                        onClick={(e) => {
                            e.stopPropagation()
                            onOpenRunning(item.runningPrograms)
                        }}
                    >
                        <RunningIcon width={27} height={25} fill="#413f5e" />
                    </span>
                )}
            </div>
            <p className="single-type-item-description">{item?.description}</p>
        </button>
    )
})

export default ContentCard

/** Placeholder tiles shown while a list loads for the first time. */
export const ContentSkeleton = ({ sections = 2, cards = 2 }) => (
    <div className="rtg-skeleton" aria-hidden="true">
        {Array.from({ length: sections }).map((_, s) => (
            <div className="single-type-section" key={s}>
                <div className="single-type-section-header">
                    <div className="rtg-skeleton-block rtg-skeleton-title" />
                    <div className="single-type-dash" style={{ borderTopColor: '#E4E3F0' }} />
                </div>
                <div className="single-type-section-items">
                    {Array.from({ length: cards }).map((__, c) => (
                        <div className="single-type-section-item rtg-skeleton-card" key={c}>
                            <div className="rtg-skeleton-block rtg-skeleton-line w-2/3" />
                            <div className="rtg-skeleton-block rtg-skeleton-line w-full mt-4" />
                            <div className="rtg-skeleton-block rtg-skeleton-line w-5/6" />
                        </div>
                    ))}
                </div>
            </div>
        ))}
    </div>
)
